/*
 *
 * AddReference
 *
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import saga from './saga';
import { RootState } from './types';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Modal from 'react-responsive-modal';
import { CircleSpinner } from 'react-spinners-kit';
import { useInjectSaga } from '../../utils/injectSaga';
import './style.css';
import { makeSelectContext } from '../Configurator/selectors/general';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';
import { CABINET_URL } from '../Configurator/constants/furniture';
import { requestGet } from '../../utils/requests';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';
import { CabinetJson } from '../../lib/modelBuilder/Data';
import { makeSelectFurniture } from '../Configurator/selectors/furniture';
import { Furniture } from '../../lib/modelBuilder/entities/Furniture';
import { updateCabinetSuccess } from '../Configurator/actions/furniture';
import * as Yup from 'yup';

interface StateProps {
  context: FurnitureEditorContext;
  furniture: Furniture | null;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  context: makeSelectContext(),
  furniture: makeSelectFurniture(),
});

function AddReference() {
  const [isOpen, setOpen] = useState(false);
  const [errors, setError] = useState<string | null>(null);
  useInjectReducer({ key: 'addReference', reducer: reducer });
  useInjectSaga({ key: 'addReference', saga: saga });
  const dispatch = useDispatch();
  const { context, furniture } = useSelector(stateSelector);
  const { t } = useTranslation();
  const form = useFormik({
    initialValues: {
      reference: '',
    },
    validationSchema: Yup.object({
      reference: Yup.number()
        .max(999999999, 'Must be 9 number or less')
        .required('The reference is required'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const url = `${CABINET_URL}/${values.reference}/`;
      setError(null);

      (requestGet(url) as Promise<CabinetJson>)
        .then((response: CabinetJson) => {
          const cabinet = Cabinet.fromModel(response);
          if (cabinet && furniture) {
            furniture.add(cabinet as Cabinet);
            dispatch(updateCabinetSuccess(furniture.clone(), null));
            setOpen(false);
            setSubmitting(false);
          }
        })
        .catch((error: string) => {
          setError(error);
          setSubmitting(false);
        });
    },
  });

  const onClose = () => {
    setOpen(false);
  };

  if (
    context === FurnitureEditorContext.webShop &&
    process.env.NODE_ENV === 'production'
  ) {
    return null;
  }

  return (
    <>
      <div className="bg-white text-sm mx-1">
        <button
          onClick={() => {
            setOpen(true);
          }}
          className="border px-3 py-1 rounded"
        >
          {t('Add reference')}
        </button>
      </div>
      <Modal open={isOpen} onClose={onClose} center>
        <div className="mt-4">
          <div className="italic text-md">
            {t('Enter the reference of the cabinet you wish to add')}
          </div>
          <form onSubmit={form.handleSubmit} className="flex flex-col">
            <input
              id="reference"
              name="reference"
              type="text"
              placeholder="401587498"
              onChange={form.handleChange}
              value={form.values.reference}
              className={`border py-2 my-1 px-3 text-lg ${
                errors || (form.touched.reference && form.errors.reference)
                  ? 'border-red'
                  : 'border-gray-400'
              }`}
            />
            {form.touched.reference && form.errors.reference ? (
              <div className="text-red-dark">{form.errors.reference}</div>
            ) : null}
            {errors && (
              <div className="text-red-dark">{t('Reference not found')}</div>
            )}
            <button
              type="submit"
              className="px-2 py-2 my-3 bg-main text-white"
              disabled={form.isSubmitting}
            >
              {!form.isSubmitting ? (
                t('Add')
              ) : (
                <div className="flex justify-center items-center">
                  <CircleSpinner size={30} color="#fff" />
                </div>
              )}
            </button>
          </form>
        </div>
      </Modal>
    </>
  );
}

export default AddReference;
